/**
 * SignalR client — per-instance live push channel for ora workflows.
 *
 * Lifted from the Outlook add-in's `signalrClient`.
 * Changes from the add-in original:
 *   - `createSignalRConnection` accepts a `getPushChannelFn` parameter
 *     instead of importing orchestratorApi directly, so both the add-in
 *     (passes orchestratorApi.getPushChannel) and ora (passes its own
 *     client-side fetch) can share this module without coupling.
 *   - Export `WORKFLOW_EVENT_METHOD` — the canonical hub method name that
 *     the substrate emits (fixed in Phase A).
 *
 * Import paths:
 *   ora    → ~/common/workflows/signalr/signalrClient
 *   add-in → @product-app/common/workflows/signalr/signalrClient
 */

import * as signalR from "@microsoft/signalr";
import type { PushChannelDescriptor } from "../types";

export const WORKFLOW_EVENT_METHOD = "wf_event";
/** Alias kept for files that import it explicitly — same value post-fix. */
export const WF_EVENT_METHOD = "wf_event";
/** Legacy hub method — kept for backward compat with any endorsement flows. */
export const SIGNALR_METHOD = "statusUpdate";

/** Sentinel thrown when the substrate signals SignalR is unavailable (503). */
export const SIGNALR_UNAVAILABLE = "SIGNALR_UNAVAILABLE";

/** Sentinel thrown when a recent connect attempt for this instance failed and
 *  we're still inside the cooldown window. Distinct from SIGNALR_UNAVAILABLE so
 *  the caller can treat it as a transient throttle (already-counted failure)
 *  rather than a fresh "SignalR is down" signal — it stops us re-negotiating
 *  (and re-minting a client token via get_push_channel) on a tight reconnect
 *  loop while still letting the consumer fall back to polling. */
export const SIGNALR_COOLDOWN = "SIGNALR_COOLDOWN";

export type GetPushChannelFn = (
  instanceId: string,
) => Promise<PushChannelDescriptor>;

/**
 * Resolver for the USER-scoped channel (`user-<oid>`). It takes no instance id
 * because the channel has none — the substrate derives the group from the
 * validated token and `/signalr/negotiate` is called with no `instance_id`.
 */
export type GetUserPushChannelFn = () => Promise<PushChannelDescriptor>;

/**
 * Map key for the user-scoped connection. Every module-level map in this file
 * (connections, refcounts, token cache, connect backoff) is keyed by instance
 * id; the user channel has no instance id, so it needs a key that CANNOT
 * collide with one. Instance ids are UUIDs, which contain neither underscores
 * nor a leading `__` — this sentinel is unrepresentable as a UUID by
 * construction, not merely unlikely.
 */
export const USER_CHANNEL_KEY = "__user__";

/** Payload delivered to the `onConnected` callback after a new WebSocket
 *  is established. Consumers use `connectionId` to join the instance's
 *  group on the local emulator, which does not honour JWT `groups` claims. */
export interface SignalRConnectedInfo {
  connectionId: string | null;
  hub: string;
}

// Module-level cache keyed by instance_id. Shared across all hook instances
// so React StrictMode double-invocations don't spawn duplicate connections.
const _connections = new Map<string, signalR.HubConnection>();

// Hub name cache — stored when a connection is first created so late joiners
// (hooks that call createSignalRConnection for an already-open instance) can
// still receive the correct hub in their onConnected callback without a
// redundant negotiate round-trip.
const _hubCache = new Map<string, string>();

// Refcount per instance_id — multiple useSignalR hooks (e.g. one in
// useActiveInstances on the active list, another in useInstancePush on an
// open detail drawer) can subscribe to the same instance. We only actually
// stop the WebSocket when the LAST holder releases. Without this, the
// drawer's unmount cleanup tears down the WS shared with the active list,
// firing onclose on the list's listener and dropping its isConnected flag
// → polling falls back to the fast cadence even though push is still
// supposed to be live.
const _refCounts = new Map<string, number>();

// Tracks which instance connections have already had onConnected fired.
// The local ASRS emulator is NOT idempotent for group joins — calling
// AddToGroupAsync twice for the same connectionId creates dual membership
// and delivers every message twice. We fire onConnected exactly once per
// connection lifecycle (new WS or after full teardown + reconnect).
const _onConnectedFired = new Set<string>();

// Per-instance connect-failure backoff. When createSignalRConnection's
// negotiate or start() throws, we stamp the failure time here; a subsequent
// acquire within _CONNECT_BACKOFF_MS short-circuits with SIGNALR_COOLDOWN
// instead of re-negotiating. Without this, a connection that can't hold makes
// every consumer re-render / re-mount re-run negotiate → a get_push_channel
// storm (each negotiate mints a fresh client token). Mirrors the per-row
// backoff in the add-in's `useActiveInstances`. Cleared on a
// successful start().
const _connectFailedAtMs = new Map<string, number>();
const _CONNECT_BACKOFF_MS = 30_000;

// In-flight connect promise per instance_id — dedupes CONCURRENT connects.
// createSignalRConnection only publishes to ``_connections`` AFTER start()
// resolves, so two hooks acquiring the same instance in the same tick (e.g. the
// active-list row in useActiveInstances + the open detail drawer in
// useInstancePush) would each miss the cache and open a SECOND WebSocket. This
// map lets the second caller await the first connect instead. Mirrors
// ``_tokenRefreshInFlight``. Cleared (rejection-safely) once the connect settles.
const _connecting = new Map<string, Promise<signalR.HubConnection>>();

// Per-instance token cache used by the SignalR client's accessTokenFactory
// (Phase 3, Gap 4). Substrate-minted client tokens have a 5-minute TTL;
// the factory is invoked by @microsoft/signalr on every reconnect and
// negotiate retry. Without this cache+refresh path, the WebSocket dies
// 5 minutes after the initial negotiate and never recovers — long HITL
// waits silently break.
interface _CachedToken {
  token: string;
  /** Wall-clock ms when this token should be refreshed (~30s before
   *  the substrate-stated expiry, to avoid a flight window where a
   *  request leaves with a token that expires before it lands). */
  refreshAtMs: number;
}
const _tokenCache = new Map<string, _CachedToken>();
/** Conservative fallback TTL when the substrate doesn't return
 *  `expires_at` in the negotiate response. Mirrors the 5-minute
 *  substrate-side default. */
const _TOKEN_DEFAULT_TTL_MS = 5 * 60_000;
/** Refresh tokens this many ms before their stated expiry. */
const _TOKEN_REFRESH_LEAD_MS = 30_000;
/** In-flight refresh promise per instance_id — coalesces concurrent
 *  factory invocations so we don't fire N parallel negotiate requests
 *  when SignalR retries quickly. */
const _tokenRefreshInFlight = new Map<string, Promise<string>>();

function _refreshAtFromExpiresAt(expiresAt: string | undefined | null): number {
  if (!expiresAt) return Date.now() + _TOKEN_DEFAULT_TTL_MS - _TOKEN_REFRESH_LEAD_MS;
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) {
    return Date.now() + _TOKEN_DEFAULT_TTL_MS - _TOKEN_REFRESH_LEAD_MS;
  }
  return parsed - _TOKEN_REFRESH_LEAD_MS;
}

function _makeAccessTokenFactory(
  instanceId: string,
  getPushChannel: GetPushChannelFn,
): () => Promise<string> {
  return async () => {
    const cached = _tokenCache.get(instanceId);
    if (cached && Date.now() < cached.refreshAtMs) {
      return cached.token;
    }
    // Coalesce concurrent refreshes so SignalR's reconnect retries don't
    // each spawn their own negotiate.
    const inFlight = _tokenRefreshInFlight.get(instanceId);
    if (inFlight) return inFlight;

    const fetchPromise = (async () => {
      try {
        const desc = await getPushChannel(instanceId);
        const fresh: _CachedToken = {
          token: desc.access_token ?? "",
          refreshAtMs: _refreshAtFromExpiresAt(desc.expires_at),
        };
        _tokenCache.set(instanceId, fresh);
        return fresh.token;
      } finally {
        _tokenRefreshInFlight.delete(instanceId);
      }
    })();
    _tokenRefreshInFlight.set(instanceId, fetchPromise);
    return fetchPromise;
  };
}

function _extractHub(url: string, fallback = "workflowstatus"): string {
  try {
    const u = new URL(url);
    // Azure SignalR negotiate URLs carry hub in ?hub= query param; fall back
    // to last path segment for emulator configurations that embed it in path.
    const fromQuery = u.searchParams.get("hub");
    if (fromQuery) return fromQuery;
    const segments = u.pathname.split("/").filter(Boolean);
    return segments[segments.length - 1] ?? fallback;
  } catch {
    return fallback;
  }
}

async function _negotiate(
  instanceId: string,
  getPushChannel: GetPushChannelFn,
): Promise<{
  url: string;
  accessToken: string | null;
  expiresAt: string | null;
  group: string;
}> {
  try {
    const desc = await getPushChannel(instanceId);
    if (!desc?.url) {
      throw new Error(SIGNALR_UNAVAILABLE);
    }
    return {
      url: desc.url,
      // access_token is optional — local emulator omits it.
      accessToken: desc.access_token || null,
      expiresAt: desc.expires_at ?? null,
      // The substrate always names the group; the fallback only covers an
      // older response shape. There is no derivable fallback for the
      // user-scoped channel — its group is `user-<oid>` and the oid is a
      // token claim this client never sees — so it stays empty rather than
      // inventing a `wf-__user__` that names nothing.
      group:
        desc.group ??
        (instanceId === USER_CHANNEL_KEY ? "" : `wf-${instanceId}`),
    };
  } catch (err) {
    const status = (err as { status?: number | null })?.status;
    if (status === 503) {
      throw new Error(SIGNALR_UNAVAILABLE);
    }
    throw err;
  }
}

/**
 * Negotiate + build + start ONE HubConnection for an instance and publish it to
 * the module cache. No refcount / onConnected bookkeeping here — that is
 * per-caller and lives in `createSignalRConnection`. This is the unit that
 * `_connecting` dedupes so concurrent acquires share a single WebSocket.
 */
async function _establishConnection(
  instanceId: string,
  getPushChannel: GetPushChannelFn,
): Promise<signalR.HubConnection> {
  // Per-instance connect backoff: if a recent attempt failed, don't re-negotiate
  // (re-minting a client token via get_push_channel) until the cooldown elapses.
  // Throw the cooldown sentinel so the caller falls back to polling instead of us
  // hammering negotiate on a tight reconnect loop. Cleared on a successful start().
  const failedAt = _connectFailedAtMs.get(instanceId);
  if (failedAt != null && Date.now() - failedAt < _CONNECT_BACKOFF_MS) {
    throw new Error(SIGNALR_COOLDOWN);
  }

  try {
    const channel = await _negotiate(instanceId, getPushChannel);

    // Seed the per-instance token cache with the token we just got from
    // negotiate so the very first WS handshake doesn't re-fetch. Subsequent
    // calls to the factory (reconnect, post-token-expiry) hit the cache and
    // refresh only when refreshAtMs is in the past.
    if (channel.accessToken) {
      _tokenCache.set(instanceId, {
        token: channel.accessToken,
        refreshAtMs: _refreshAtFromExpiresAt(channel.expiresAt),
      });
    }

    // accessTokenFactory is invoked on every WS handshake (initial + each
    // reconnect). When SignalR's ~5-minute token TTL is about to expire,
    // the factory transparently re-negotiates via getPushChannel — long
    // HITL waits and laptop-sleep recoveries no longer need a page reload.
    // Local-emulator setups that omit the access token entirely keep the
    // legacy behaviour (no factory wired).
    const urlOptions = channel.accessToken
      ? {
          accessTokenFactory: _makeAccessTokenFactory(instanceId, getPushChannel),
          // Let @microsoft/signalr run its negotiate against the Azure SignalR CLIENT
          // endpoint (POST https://<svc>.service.signalr.net/client/negotiate?hub=wf).
          // That negotiate returns the connection id/token that REGISTERS the
          // connection server-side. We briefly used skipNegotiation:true to dodge a
          // negotiate 404 — but that 404 was actually the substrate's url=null bug
          // (since fixed). Skipping negotiate yields a socket with connectionId=null
          // that Azure SignalR never fully registers, so it drops the connection and
          // the client reconnect-loops (re-fetching the token every retry; observed as
          // rapid repeated get_push_channel calls). Negotiating produces a real
          // connectionId and a connection that holds. Pin the transport to WebSockets
          // so we don't fall back to SSE/long-polling. The local-emulator path (no
          // accessToken) keeps SignalR defaults, which the emulator handles.
          transport: signalR.HttpTransportType.WebSockets,
        }
      : {};
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(channel.url, urlOptions)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    const hub = _extractHub(channel.url);
    await connection.start();

    // Connected — clear any prior failure backoff and publish to the cache so
    // the next acquire (and any concurrent waiter) reuses this socket.
    _connectFailedAtMs.delete(instanceId);
    _connections.set(instanceId, connection);
    _hubCache.set(instanceId, hub);
    return connection;
  } catch (err) {
    // Stamp the failure so rapid re-acquires back off (SIGNALR_COOLDOWN) instead
    // of re-negotiating. SIGNALR_UNAVAILABLE (503 / missing url) flows through
    // here too — useSignalR maps that to the polling fallback.
    _connectFailedAtMs.set(instanceId, Date.now());
    throw err;
  }
}

/**
 * Acquire (or build) the HubConnection for a workflow instance and bump the
 * refcount. Every call must be paired with a `stopConnection(instanceId)`.
 * Concurrent acquires for the same instance share ONE WebSocket (see
 * `_connecting`). Reconnect backoff: 0 ms, 2 s, 5 s, 10 s, 30 s — matches v1.
 */
export async function createSignalRConnection(
  instanceId: string,
  getPushChannel: GetPushChannelFn,
  onConnected?: (info: SignalRConnectedInfo) => void,
): Promise<signalR.HubConnection> {
  if (!instanceId) throw new Error("createSignalRConnection: instanceId required");

  // Resolve the underlying connection, deduping concurrent connects so two hooks
  // acquiring the same instance in the same tick share ONE socket instead of
  // racing to open two.
  let connection = _connections.get(instanceId);
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    let pending = _connecting.get(instanceId);
    if (!pending) {
      pending = _establishConnection(instanceId, getPushChannel);
      _connecting.set(instanceId, pending);
      // Clear the in-flight marker once settled, on a separate rejection-safe
      // chain so a failed connect doesn't surface as an unhandled rejection —
      // the real callers handle the outcome via their own `await pending` below.
      void pending
        .catch(() => undefined)
        .finally(() => {
          if (_connecting.get(instanceId) === pending) _connecting.delete(instanceId);
        });
    }
    connection = await pending;
  }

  // Per-caller bookkeeping. Every acquire bumps the refcount so the WS is only
  // torn down when the LAST holder releases. Fire onConnected exactly once per
  // connection lifecycle (the first caller — new socket or late joiner) so the
  // emulator group-join isn't issued twice for one connectionId.
  _refCounts.set(instanceId, (_refCounts.get(instanceId) ?? 0) + 1);
  if (onConnected && !_onConnectedFired.has(instanceId)) {
    _onConnectedFired.add(instanceId);
    onConnected({
      connectionId: connection.connectionId,
      hub: _hubCache.get(instanceId) ?? "workflowstatus",
    });
  }
  return connection;
}

/**
 * Acquire (or build) the ONE user-scoped HubConnection and bump its refcount.
 * Every call must be paired with a `stopUserConnection()`.
 *
 * Deliberately a thin adapter over `createSignalRConnection` rather than a
 * second implementation: the refcount, the concurrent-connect dedupe, the
 * token refresh factory and the connect backoff are all bookkeeping this
 * channel needs identically, and a forked copy is how the two channels end up
 * with different reconnect behaviour that only shows up in production.
 */
export async function createUserSignalRConnection(
  getUserPushChannel: GetUserPushChannelFn,
  onConnected?: (info: SignalRConnectedInfo) => void,
): Promise<signalR.HubConnection> {
  return createSignalRConnection(
    USER_CHANNEL_KEY,
    () => getUserPushChannel(),
    onConnected,
  );
}

/** Release one reference to the user-scoped connection. */
export async function stopUserConnection(): Promise<void> {
  await stopConnection(USER_CHANNEL_KEY);
}

export function getUserConnection(): signalR.HubConnection | null {
  return _connections.get(USER_CHANNEL_KEY) ?? null;
}

export function getConnection(instanceId: string): signalR.HubConnection | null {
  return _connections.get(instanceId) ?? null;
}

/**
 * Release one reference to the connection for `instanceId`. The WebSocket is
 * only actually stopped when the refcount reaches zero — until then, other
 * subscribers continue receiving events on the same socket.
 */
export async function stopConnection(instanceId: string): Promise<void> {
  const count = _refCounts.get(instanceId) ?? 0;
  if (count > 1) {
    _refCounts.set(instanceId, count - 1);
    return;
  }
  // Last holder — actually tear down.
  _refCounts.delete(instanceId);
  _tokenCache.delete(instanceId);
  _tokenRefreshInFlight.delete(instanceId);
  _hubCache.delete(instanceId);
  _onConnectedFired.delete(instanceId);
  _connectFailedAtMs.delete(instanceId);
  _connecting.delete(instanceId);
  const conn = _connections.get(instanceId);
  if (!conn) return;
  _connections.delete(instanceId);
  try {
    await conn.stop();
  } catch {
    // best-effort
  }
}

export async function stopAllConnections(): Promise<void> {
  const ids = Array.from(_connections.keys());
  // Force teardown regardless of refcount — used by global cleanup paths
  // (logout, page hide). Reset refcounts so any in-flight acquire promise
  // that resolves after this won't leak a permanent +1.
  _refCounts.clear();
  _tokenCache.clear();
  _tokenRefreshInFlight.clear();
  _hubCache.clear();
  _onConnectedFired.clear();
  _connectFailedAtMs.clear();
  _connecting.clear();
  await Promise.all(
    ids.map(async (id) => {
      const conn = _connections.get(id);
      if (!conn) return;
      _connections.delete(id);
      try {
        await conn.stop();
      } catch {
        // best-effort
      }
    }),
  );
}

/** Test-only: reset module-level connection cache. */
export function _resetSignalRClientForTests(): void {
  _connections.clear();
  _refCounts.clear();
  _tokenCache.clear();
  _tokenRefreshInFlight.clear();
  _hubCache.clear();
  _onConnectedFired.clear();
  _connectFailedAtMs.clear();
  _connecting.clear();
}
